<template>
    <q-table class="shadow-25 v-list bg-white"
             style="width:90vw; max-width: 100%;"
             :pagination.sync="pagination"
             :loading="isLoading"
             @request="refreshRows"
             :data.sync="results.data"
             :hide-header="results.data.length == 0"
             :columns="translatedColumns"
             :filter="filter"
             :visible-columns="visibleColumns"
             :rows-per-page-options="options.rowsPerPageOptions"
             row-key="id">
        <v-header :title="plural" :icon="icon"
                  slot="top" slot-scope="props">
            <template slot="right">
                {{this.$slots}}
                <v-button v-if="createRoute"
                          flat round dense color="primary"
                          icon="add circle outline"
                          @click="$router.push({name: createRoute})"/>
                <v-button v-show="!isActiveToolbar('filter') && searchActive"
                          flat round dense no-caps color="primary"
                          icon="search"
                          dusk="search-icon"
                          @click="toggleToolbar('filter')">
                    <q-tooltip v-show="!isActiveToolbar('filter')"
                               anchor="top left" self="bottom middle">
                        {{ $T('search') }}
                    </q-tooltip>
                </v-button>
                <q-input v-if="isActiveToolbar('filter')"
                         hide-underline autofocus color="primary"
                         v-model="tempFilter"
                         :placeholder="$T('search')"
                         @input="updateFilter"
                         class="bg-search"
                         :before="[{icon: 'search', handler () { toggleToolbar('filter') }}]"/>
                <v-button v-show="!isActiveToolbar('columns') && columnsOptions.length > 0"
                          flat round dense no-caps color="primary"
                          icon="view column"
                          dusk="column-icon"
                          @click="toggleToolbar('columns')">
                    <q-tooltip v-show="!isActiveToolbar('columns')"
                               anchor="top left" self="bottom middle">
                        {{ $T('columns') }}
                    </q-tooltip>
                </v-button>
                <select-extended v-show="isActiveToolbar('columns')"
                                 select-all multiple toggle hide-underline
                                 color="primary"
                                 v-model="visibleColumns"
                                 :options="columnsOptions"
                                 :display-value="$T('columns')"
                                 :before="[{icon: 'view column', handler () { toggleToolbar('columns') }}]"/>
                <v-table-filter :slug="slug" @refreshTable="useFilters" />
                <v-button v-if="exportRoute"
                          flat round dense no-caps color="primary"
                          icon="cloud download"
                          dusk="export-icon"
                          @click="handleExport">
                    <q-tooltip anchor="top left" self="bottom middle">
                        {{ $T('export') }}
                    </q-tooltip>
                </v-button>
                <v-button v-if="exportStockOrders"
                          flat round dense no-caps color="primary"
                          icon="cloud download"
                          dusk="export-icon"
                          @click="handleExportStockOrders">
                    <q-tooltip anchor="top left" self="bottom middle">
                        {{ $T('export stock orders') }}
                    </q-tooltip>
                </v-button>
                <v-button v-if="exportChangeStatusRoute"
                          flat round dense no-caps color="primary"
                          icon="cloud download"
                          dusk="export-icon"
                          @click="handleExportChangeStatus">
                    <q-tooltip anchor="top left" self="bottom middle">
                        {{ $T('export stock orders change status') }}
                    </q-tooltip>
                </v-button>
                <v-button v-if="importRoute"
                          flat round dense no-caps color="primary"
                          icon="cloud upload"
                          dusk="import-icon"
                          @click="$router.push({ name: importRoute , params: importParams})">
                    <q-tooltip anchor="top left" self="bottom middle">
                        {{ $T('import') }}
                    </q-tooltip>
                </v-button>
            </template>
        </v-header>
        <q-tr slot="header" slot-scope="props" :props="props">
            <q-th v-for="col in props.cols" :key="col.name" :props="props" v-if="!col.hide">
                {{ $t(col.label) }}
            </q-th>
        </q-tr>
        <q-tr v-if="rowClick" :slot="rowClick &&  'body'" slot-scope="props" :props="props"
              @click.native="rowClick(props.row)" class="cursor-pointer">
            <q-td v-for="col in props.cols" :props="props" :key="col.name" v-if="!col.hide">
                <q-chip v-if="col.name == 'stock_order_status.description' || col.name == 'stock_operation.description' "
                        :dusk="col.value"
                        small :color="colorFor(col.value)">{{ $T(col.value.toLowerCase())}} {{$T(getStockOperationLabel(props.cols))}}
                </q-chip>

                <q-chip v-else-if="col.name == 'order_status.description'"
                        :dusk="col.value"
                        small :color="colorFor(col.value)">{{ $T(col.value.toLowerCase()) }}
                </q-chip>

                <q-chip v-else-if="col.name == 'stock_status.description'"
                        :dusk="col.value"
                        small :color="colorFor(col.value)">{{ $T(col.value.toLowerCase()) }}
                </q-chip>

                <q-chip v-else-if="col.name == 'identifier'" small :class="{'bg-blue': ['Yes', 'Sim'].includes(props.row.is_circular_lending) }"> {{ col.value }}</q-chip>

                <img v-else-if="col.value && (col.name == 'lending_kit.lending_kit_color.image' || col.name == 'lending_kit_color.image')" height="50px" :src="`/storage/uploads/${col.value}`" />
                     
              <template v-else>{{ col.value }}</template>
            </q-td>
        </q-tr>

        <q-td slot="body-cell-order_status.description" slot-scope="props" :props="props">
            <q-chip small :color="colorFor(props.value)">{{ props.value }}</q-chip>
        </q-td>
        <q-td slot="body-cell-lending_kit.lending_kit_color.image" slot-scope="props" :props="props">
          <img v-if="props.value" height="50px" :src="`/storage/uploads/${props.value}`" />
        </q-td>
        <q-td slot="body-cell-lending_kit_color.image" slot-scope="props" :props="props">
          <img v-if="props.value" height="50px" :src="`/storage/uploads/${props.value}`" />
        </q-td>
    </q-table>
</template>

<script>
    import {
        debounce,
        QTable,
        QTr,
        QTd,
        QInput,
        QToggle,
        QPopover,
        QChip,
        QTooltip
    } from 'quasar-framework'
    import axios from 'axios'
    import {userPreferences} from '../store/modules/userPreferences'
    import {mapGetters} from 'vuex'
    import VTableFilter from './../components/form/VTableFilter.vue'

    const PER_PAGE = 10

    const PAGINATION_QUANTITIES = [10, 20, 50, 100]

    export default {

        metaInfo() {
           return !this.$route.params.activateMeta ? {title: this.updateMetaInfo ? (this.isLoading ? this.$T('loading') + ' ' : '') + (this.plural) : document.title} : {}
        },
        
        components: {
            QTable,
            QTr,
            QTd,
            QInput,
            QToggle,
            QPopover,
            QChip,
            QTooltip,
            VTableFilter
        },

        props: {
            plural: {
                type: String,
                required: true
            },
            model: {
              type: String,
              required: false
            },
            singular: {
                type: String,
                required: true
            },
            slug: {
                type: String,
                required: true
            },
            icon: {
                type: String,
                required: true
            },
            columns: {
                type: Array,
                required: true
            },
            createRoute: {
                type: String
            },
            rowClick: {
                type: Function
            },
            exportRoute: {
                type: String,
                default: null
            },
            exportStockOrders: {
                type: String,
                default: null
            },
            exportChangeStatusRoute: {
                type: String,
                default: null
            },
            exportParams: {
                type: Object,
                default:() => {}
            },
            importRoute: {
                type: String,
                default: null
            },
            importParams: {
                type: Object,
                default:() => {}
            },
            updateMetaInfo: {
                type: Boolean,
                default: true
            },
            updateUrlPagination: {
                type: Boolean,
                default: true
            },            
            searchActive: {
              type: Boolean,
              default: false
            },
        },

        data() {
            return {
                activeToolbar: this.$route.query.search ? 'filter' : '',
                options: {
                    plural: this.plural,
                    isTrash: false,
                    singular: this.singular,
                    rowsPerPageOptions: PAGINATION_QUANTITIES,
                    resourcePath: `${config.appUrl}/api/v1/${this.slug}`,
                    columns: this.columns,
                },
                exportRelation: {
                  relationId: null, // Inicialize com null ou o valor padrão desejado
                },
                filtersChosen: null,
                start: false,
                isLoading: false,
                pagination: {},
                filter: '',
                tempFilter: '',
                results: {
                    meta: {
                        total: 0,
                        total_unfiltered: 0,
                        total_in_trash: 0,
                    },
                    data: [],
                }
            }
        },

        methods: {
            handleExport() {
                const url = this.$route.fullPath;
                const match = url.match(/\/(\d+)\?/);

                if (match && match[1]) {
                    const value = match[1];
                    this.exportRelation.relationId = value;
                }
                this.$router.push({ name: this.exportRoute, params: this.exportRelation });
            },
            handleExportStockOrders() {
                const url = this.$route.fullPath;
                const match = url.match(/\/(\d+)\?/);

                if (match && match[1]) {
                    const value = match[1];
                    this.exportRelation.relationId = value;
                }
                this.$router.push({ name: this.exportStockOrders, params: this.exportRelation });
            },
            handleExportChangeStatus() {
                const url = this.$route.fullPath;
                const match = url.match(/\/(\d+)\?/);

                if (match && match[1]) {
                    const value = match[1];
                    this.exportRelation.relationId = value;
                }
                this.$router.push({ name: this.exportChangeStatusRoute, params: this.exportRelation });
            },
            getStockOperationLabel(cols) {
                let col = cols.find( (col) => col.name === 'stock_operation.description')
                if ( col ) {
                    return " - "+this.$T(col.value)
                }
            },

          updateFilter: debounce(function () {
            this.searcher = this.model;
            this.filter = this.tempFilter
          }, 500),

          useFilters(filters){
            this.filtersChosen = filters
            this.refreshRows()
          },

            getPaginationFromQuery() {
                if (!this.options) return
                let page = parseInt(getNested('page', this.$route.query, 1))
                let rowsPerPage = parseInt(getNested('per_page', this.$route.query, PER_PAGE))
                let pagination = {
                    page,
                    rowsPerPage,
                    sortBy: getNested('sort_by', this.$route.query, null), // String, column "name" property value
                    descending: getNested('descending', this.$route.query, true).toString() === 'true' ? true : false,
                    rowsNumber: this.pagination.rowsNumber || page * rowsPerPage // specifying this determines pagination is server-side
                }
                let filter = getNested('search', this.$route.query, '')

                if (objectToString(pagination) !== objectToString(this.pagination) || this.filter !== filter) {
                    this.filter = filter
                    this.pagination = pagination
                }
            },

          async refreshRows({pagination, filter} = {pagination: null, filter: null}) {
              if(this.model !== this.searcher && !this.start){
                this.filter = '';
                return;
              }

              this.searcher = '';
              this.searchActive = true;
              if (!pagination) pagination = this.pagination
              if (!filter) filter = this.filter

              let query = { // ETL from component to API
                page: pagination.page,
                sort_by: pagination.sortBy,
                descending: pagination.descending,
                per_page: pagination.rowsPerPage,
                search: filter
              }

              if(this.filtersChosen) {
                query = {
                  ...query,
                  ...this.filtersChosen
                }
              }

              // QTable does not update the pagination and filter models directly
              this.pagination = pagination
              this.tempFilter = this.filter = filter

              if(this.updateUrlPagination) {
                if (objectToString(this.$route.query) === '{}' || pagination.page === 0)
                    this.$router.replace({query: query})
                else if (objectToString(query) !== objectToString(this.$route.query))
                    this.$router[this.infiniteScroll === true ? 'replace' : 'push']({query: query})
              }

              await this.request('get', `${config.appUrl}/api/v1/${this.slug}`, {params: query})
                  .then((response) => {
                      // we also set (or update) rowsNumber
                      this.pagination.rowsNumber = response.meta.total

                      // then we update the rows with the fetched ones
                      this.results.meta = response.meta
                      this.results.links = response.links
                      if (response.meta.current_page == 1 || !this.infiniteScroll) {
                          this.results.data = response.data
                      } else {
                          response.data.forEach(r => this.results.data.push(r))
                      }

                      const openModal = localStorage.getItem("openModal");

                      if(openModal) {
                        const item = response.data.find(el => el.id == openModal)
                        if(item) {
                            this.rowClick(item)
                        } else {
                            this.request('get', `${config.appUrl}/api/v1/${this.slug}`, {params: {
                                search: openModal
                            }})
                            .then((response) => {
                                const item = response.data.find(el => el.id == openModal)
                                if(item) {
                                    this.rowClick(item)
                                }
                            })
                        }

                        localStorage.removeItem("openModal");
                      }
                  })


          },

            /**
             * Submit a request.
             *
             * @param {string} requestType
             * @param {string} url
             */
            request(requestType, url, data = {}) {
                this.isLoading = true

                return new Promise((resolve, reject) => {
                    axios[requestType](url, data)
                        .then(response => {
                            this.onSuccess(response.data);

                            resolve(response.data);
                        })
                        .catch(error => {
                            this.onFail(error.response.data);

                            reject(error.response.data);
                        })
                });
            },

            /**
             * Handle a successful request.
             *
             * @param {object} data
             */
            onSuccess(data) {
                this.isLoading = false
            },

            /**
             * Handle a failed request.
             *
             * @param {object} errors
             */
            onFail(data) {
                this.isLoading = false

                if (data && data.error)
                    this.notify(this.$T('error'), data.error.message, [], 'negative')
            },

            async describe() {
              await this.$store.dispatch('crud/fetchList', this.slug)
            },

            async init() {
                this.filtersChosen = null
                await this.describe()
                this.results.data = []
                this.getPaginationFromQuery()
                this.start = true
                if((!this.$route.params.stock_order || this.$route.params.stock_order == 'undefined') &&
                    !this.matchRoleLendingPoint(this.user.role.name)){
                    this.refreshRows();
                }
            },

            isActiveToolbar(item) {
                return this.activeToolbar == item
            },

            toggleToolbar(item) {
                this.activeToolbar = this.activeToolbar == item ? null : item
            },

            colorFor(status) {
                let colors = []
                colors['Reserved'] = 'primary'
                colors['In Lending'] = 'info'
                colors['In Transit'] = 'tertiary'
                colors['Cancelled'] = 'negative'
                colors['Pending Pick-up'] = 'warning'
                colors['Completed'] = 'positive'

                // stock Order
                colors['Pending'] = 'info'
                colors['In transit'] = 'tertiary'
                colors['Cancelled'] = 'negative'
                colors['Completed'] = 'positive'


                return colors[status]
            },

            matchRoleLendingPoint(userRole) {
              return ['Lending Point Hostesses'].includes(userRole)
            },
        },

        mounted() {
            this.init()
        },

        watch: {
            $route: 'getPaginationFromQuery'
        },

        computed: {
            translatedColumns() {
                if(this.matchRoleLendingPoint(this.user.role.name)) this.refreshRows();
                return this.columns.map(column => {
                    return {
                        name: column.field,
                        label: this.$T(column.label),
                        field: column.field,
                        align: column.align || 'left',
                        sortable: column.sortable || false,
                        required: column.required || false,
                        hide: column.hide || false
                    }
                })
            },

            columnsOptions() {
                return this.options.columns.filter(c => !c.required).map(c => ({value: c.field, label: this.$T(c.label)}))
            },

            visibleColumns: userPreferences('visibleColumns', []),

            baseRoute() {
                return this.slug.split('/').join('-');
            },

            ...mapGetters({
                user: 'auth/user'
            }),
        }
    }
</script>



// WEBPACK FOOTER //
// resources/assets/js/pages/_list.vue